import React from 'react';
import { Grid } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import InfoField from './InfoField';

interface FieldConfig {
  id: string;
  name: string;
  displayName: string;
  fields: {
    ExpandedViewItemKey: { value: string };
    ExpandedViewItemLabel: { value: string };
  };
}

interface TrackingParametersProps {
  trackingParams: Record<string, any>;
  fieldConfigs?: FieldConfig[];
  headerText?: string;
}

const TrackingParametersSection: React.FC<TrackingParametersProps> = ({
  trackingParams,
  fieldConfigs,
  headerText,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  // Default field configurations if not provided from Sitecore
  const defaultFields = [
    { key: 'utm_source', label: 'UTM Source' },
    { key: 'utm_medium', label: 'UTM Medium' },
    { key: 'utm_campaign', label: 'UTM Campaign' },
    { key: 'utm_term', label: 'UTM Term' },
    { key: 'utm_content', label: 'UTM Content' },
    { key: 'sID', label: 'sID' },
    { key: 'psecID', label: 'psecID' },
    { key: 'gclid', label: 'GCLID' },
    { key: 'fbclid', label: 'FBCLID' },
    { key: 'atid', label: 'ATID' },
  ];

  const fields = fieldConfigs
    ? fieldConfigs.map((config) => ({
        key: config.fields.ExpandedViewItemKey.value,
        label: config.fields.ExpandedViewItemLabel.value,
      }))
    : defaultFields;

  const parsedTrackingParams: Record<string, string> = {};

  if (trackingParams?.trackingParameter) {
    try {
      const trackingArray = JSON.parse(trackingParams.trackingParameter);
      if (Array.isArray(trackingArray)) {
        trackingArray.forEach((item: string) => {
          const colonIndex = item.indexOf(':');
          if (colonIndex > -1) {
            const key = item.substring(0, colonIndex).trim();
            const value = item.substring(colonIndex + 1).trim();
            parsedTrackingParams[key] = value;
          }
        });
      }
    } catch (error) {
      console.error('Error parsing tracking parameters:', error);
    }
  }

  const hasTrackingParams = fields.some((field) => parsedTrackingParams[field.key]);

  if (!hasTrackingParams) return null;

  return (
    <CustomAccordion
      title={headerText || 'Tracking Parameters'}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
      }}
      detailsSx={{ py: 1, px: 2 }}
    >
      <Grid container spacing={1.5}>
        {fields.map((field, index) => {
          const value = parsedTrackingParams[field.key];
          if (!value) return null;

          return (
            <Grid item xs={12} sm={6} md={3} key={field.key || index}>
              <InfoField label={field.label} value={value} />
            </Grid>
          );
        })}
      </Grid>
    </CustomAccordion>
  );
};

export default TrackingParametersSection;
